import { useState, useCallback, useEffect } from "react";
import {
  trackMarketplaceBannerImpression,
  trackMarketplaceBannerClick,
} from "../utils/gtm";

const buildBannerParams = (banner, bannerIndex) => ({
  index: bannerIndex,
  custom_title:
    banner.custom_title ||
    banner.title ||
    banner.name ||
    `Banner ${bannerIndex + 1}`,
  content_type: banner.item_type || "banner",
  screenName: banner.item_type || "home",
  data_inicio: banner.start_date,
  data_termino: banner.end_date,
  url_imagem: banner.s3_file,
});

/**
 * Custom hook to manage banner tracking in carousels
 *
 * @param {Array} banners - Array of banners
 * @param {string} _channel - Deprecated, kept for backward compatibility (channel is handled by gtm.js)
 * @param {React.MutableRefObject} firstBannerSent - Ref to control if first banner has been sent
 * @returns {Object} Object with handleBannerView, handleBannerClick, userInteraction, setUserInteraction
 */
export const useBannerTracking = (banners, channel, firstBannerSent) => {
  const [userInteraction, setUserInteraction] = useState(false);

  const handleBannerView = useCallback(
    (eventIndex, direction) => {
      if (!userInteraction) return;

      const banner = banners[eventIndex];
      if (!banner) return;

      trackMarketplaceBannerImpression(buildBannerParams(banner, eventIndex));
      setUserInteraction(false);
    },
    [banners, channel, userInteraction],
  );

  useEffect(() => {
    if (banners && banners.length > 0 && !firstBannerSent.current) {
      const banner = banners[0];
      if (banner) {
        trackMarketplaceBannerImpression(buildBannerParams(banner, 0));
        firstBannerSent.current = true;
      }
    }
  }, [banners, firstBannerSent]);

  const handleBannerClick = useCallback(
    (bannerIndex) => {
      const banner = banners[bannerIndex];
      if (!banner) return;

      trackMarketplaceBannerClick(buildBannerParams(banner, bannerIndex));
    },
    [banners, channel],
  );

  return {
    handleBannerView,
    handleBannerClick,
    userInteraction,
    setUserInteraction,
  };
};
